<script setup lang="ts">
import {computed} from 'vue';
import type {BadgeDTO} from '@/ts/types/dto/badge-dto';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {buildImageTransformUrl} from '@/ts/utils/pure-functions';

const props = defineProps<{
  badge: BadgeDTO;
}>();

// 150px box at most; width budget is 2x for hi-DPI. Handles UUIDs and full Directus URLs alike
const badgeImageUrl = computed(() => buildImageTransformUrl(props.badge.imageUrl, {width: 320, format: 'webp'}));

const isOpen = defineModel<boolean>();

const emit = defineEmits<{
  viewBadges: [];
}>();

function onViewBadges(): void {
  isOpen.value = false;
  emit('viewBadges');
}
</script>

<template>
  <balm-dialog v-model="isOpen" type="fit-content" theme="neutral-0">
    <div class="badge-earned-modal">
      <div class="badge-image-container">
        <q-img
          :src="badgeImageUrl"
          :alt="badge.title"
          class="badge-image"
          fit="contain"
        >
          <template #loading>
            <q-skeleton class="badge-image-loading" type="QAvatar"/>
          </template>
        </q-img>
      </div>

      <div class="badge-content">
        <h2 class="congratulations">
          {{ $t('badge_earned_congratulations') }}
        </h2>
        <p class="badge-name">
          {{ badge.title }}
        </p>
        <p v-if="badge.description" class="badge-description">
          {{ badge.description }}
        </p>
      </div>

      <div class="badge-actions">
        <balm-btn
          :label="$t('badge_earned_view_badges')"
          @click="onViewBadges"
        />
      </div>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.badge-earned-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--Spacing-spacing-6);
  text-align: center;
  gap: var(--Spacing-spacing-4);
  min-width: 300px;

  @include screen-md {
    min-width: 400px;
    padding: var(--Spacing-spacing-8) var(--Spacing-spacing-8) var(--Spacing-spacing-6);
    gap: var(--Spacing-spacing-6);
  }
}

.badge-image-container {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;

  @include screen-md {
    width: 150px;
    height: 150px;
  }

  .badge-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.badge-image-loading {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.badge-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-2);
}

.congratulations {
  @include font-display-3;
  color: var(--Functional-wl-completed-surface);
  margin: 0;
}

.badge-name {
  @include font-display-5;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
}

.badge-description {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-60);
  margin: 0;
}

.badge-actions {
  margin-top: var(--Spacing-spacing-2);
}
</style>
